import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  ElementRef,
  afterNextRender,
  inject,
  input,
  output,
  signal,
  computed,
  effect,
  viewChildren,
  TemplateRef,
  untracked,
  Injector,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ItemField } from '~/core/services/item-fields/item-fields';
import { resolveItemNavigationPath } from '~/core/services/item-fields/resolve-item-navigation-path';
import { IdentifierType, PositionType } from '~/core/enum/widgets.enum';
import { MenuType } from '~/core/constants/sport-display';
import { MenuTopBarItemComponent } from '~/components/menu-top-bar-item/menu-top-bar-item.component';
import { HeaderDropdownMenuComponent } from '~/components/header-dropdown-menu/header-dropdown-menu.component';
import { ClickOutsideDirective } from '~/core/directives/click-outside.directive';
import { CompanyStore } from '~/core/store/company.store';
import { SportsbookProvider } from '~/core/enum/providers.enum';

/**
 * Item types that are never moved to the overflow dropdown, regardless of available space.
 * LOGO (brand/home) and AUTH (login/profile area) must always be visible on the main row.
 */
const PINNED_MENU_TYPES = new Set<string>([MenuType.LOGO, MenuType.AUTH]);

/**
 * Approximate rendered width (px) of the "More" trigger button.
 * Used to reserve space in the budget calculation so the button itself doesn't
 * cause a second overflow pass after it appears.
 * Desktop shows label + icon; mobile shows icon only.
 */
const MORE_BTN_WIDTH_DESKTOP = 72;
const MORE_BTN_WIDTH_MOBILE = 36;

@Component({
  selector: 'app-header-overflow-row',
  templateUrl: './header-overflow-row.component.html',
  styleUrls: ['./header-overflow-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MenuTopBarItemComponent, MatIcon, NgTemplateOutlet, HeaderDropdownMenuComponent, ClickOutsideDirective, TranslatePipe],
})
/**
 * Renders a single header row that intelligently hides overflowing items into a
 * "More" dropdown, while maintaining left / center / right alignment.
 *
 * ## Layout model
 * The row is a `flex-nowrap` container structured as:
 *
 *   [LEFT items] [spacer flex:1] [CENTER items] [More button?] [spacer flex:1] [RIGHT items]
 *
 * The two `flex: 1` spacers act as elastic gutters: when all items fit they share
 * the leftover space equally, pushing each group to its respective edge. If
 * CENTER is empty the two spacers simply merge and the other groups stay at
 * their edges. This replaces the old 3-column approach where each column was
 * independently constrained to 1/3 of the row width.
 *
 * ## Overflow lifecycle
 * 1. **Reset** – when `items` changes, all items are set as visible and
 *    `_measuring` is set to `true` (hides them via CSS to prevent a flash).
 * 2. **Measure** – after the next render, `_measureAndRecalculate()` reads the
 *    `offsetWidth` of each rendered `#itemEl` and stores it in `_widthCache`
 *    keyed by `item._id` (order-independent).
 * 3. **Recalculate** – `_recalculate()` computes whether all items fit in the
 *    container. If not, it removes non-pinned items from the end (preserving
 *    pinned items) until the remaining set fits within the available budget
 *    (container width minus the More button width).
 * 4. **Observe** – a `ResizeObserver` watches the host element and re-runs
 *    `_recalculate()` (debounced via `requestAnimationFrame`) on every resize.
 *
 * ## Pinned items
 * Items with `MenuType.LOGO` or `MenuType.AUTH` are pinned and never moved to
 * the dropdown. If even pinned items alone cannot fit, the algorithm still keeps
 * them visible and hides everything else.
 *
 * ## Gap accounting
 * The flex container has N items + 2 spacers = N+2 flex children → N+1 gaps.
 * A naïve `reduce` over items only counts N-1 item-to-item gaps, missing the
 * 2 gaps adjacent to the spacers. All width checks therefore add `2 * gap`.
 * Similarly, `moreBtnWidth` includes `+gap` for the gap slot the More button
 * introduces as an extra flex child.
 */
export class HeaderOverflowRowComponent {
  private readonly hostEl = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly companyStore = inject(CompanyStore);
  private readonly translateService = inject(TranslateService);

  /**
   * All items for this row in a flat array, sorted by `order`.
   * Each item carries its own `position` (LEFT | CENTER | RIGHT) which the
   * template uses to place it in the correct group. This replaces the old
   * three-column approach where items were pre-split by the parent.
   */
  readonly items = input<ItemField[]>([]);

  /**
   * When `true`, the "More" trigger renders icon-only (≈36 px).
   * When `false` (desktop), it renders label + icon (≈72 px).
   * Also affects the budget reserved in the width calculation.
   */
  readonly isMobile = input(false);

  /**
   * Optional template projected into AUTH-type items.
   * Pass `<app-authentication-field>` from the parent so the login / profile
   * widget renders inside the correct item slot.
   */
  readonly loginTemplate = input<TemplateRef<unknown> | null>(null);

  // ── Pass-through inputs forwarded to every app-menu-top-bar-item ───────────
  readonly isCasinoOpened = input(false);
  readonly isLightTheme = input(false);
  readonly isLoggedIn = input(false);
  readonly showThemeTooltip = input(true);
  readonly hideButtonLightMode = input(false);
  readonly labelClass = input('menu-top-bar__option-label');
  readonly showCrmButton = input(false);
  readonly activeUrl = input<string>('');
  readonly inSubheader = input(false);

  // ── Outputs forwarded from child items ──────────────────────────────────────
  readonly itemClick = output<ItemField>();
  readonly themeToggle = output<void>();

  /**
   * Set of item IDs whose resolved navigation path matches (or is a prefix of)
   * the current URL. Used to highlight the active item in both the row and the
   * dropdown without re-evaluating per item in the template.
   */
  readonly _activeItemIds = computed(() => {
    const currentPath = this.activeUrl();
    const ids = new Set<string>();
    if (!currentPath) return ids;
    for (const item of this.items()) {
      const itemPath = resolveItemNavigationPath(item);
      if (itemPath && (currentPath === itemPath || currentPath.startsWith(itemPath + '/'))) {
        ids.add(item._id);
      }
    }
    return ids;
  });

  /**
   * Map of item ID → resolved Angular router path (or `null` for items that
   * are not navigable, e.g. Betby BETS items which open an iframe instead).
   * Passed directly to `app-menu-top-bar-item` and `app-header-dropdown-menu`.
   */
  readonly _routerPaths = computed(() => {
    const map = new Map<string, string | null>();
    const provider = this.companyStore.viewConfigSignal()?.sportsbook_screen?.provider;
    const isProviderBets = provider === SportsbookProvider.BETBY || provider === SportsbookProvider.FIRST;
    const isFirstProvider = provider === SportsbookProvider.FIRST;
    for (const item of this.items()) {
      const menuType = item.action?.menu_type;
      const shouldUseAction =
        (isProviderBets && menuType === MenuType.BETS) ||
        (isFirstProvider && [MenuType.SPORTS, MenuType.LIVE, MenuType.BONUS].includes(menuType));
      map.set(item._id, shouldUseAction ? null : resolveItemNavigationPath(item));
    }
    return map;
  });

  /**
   * Map of item ID → active boolean, derived from `_activeItemIds`.
   * Avoids calling `.has()` on the set inside the `@for` template loop,
   * keeping template expressions simple.
   */
  readonly activeMap = computed(() => {
    const map = new Map<string, boolean>();
    const activeIds = this._activeItemIds();
    for (const item of this.items()) {
      map.set(item._id, activeIds.has(item._id));
    }
    return map;
  });

  /**
   * Set of item IDs that are AUTH-type menu items.
   * Used in the template to decide whether to project `loginTemplate` into
   * a given item slot, without needing a function call per item.
   */
  readonly _loginItemIds = computed(() => {
    const ids = new Set<string>();
    for (const item of this.items()) {
      if (item.identifier_type === IdentifierType.MENU_TYPE && item.action?.menu_type === MenuType.AUTH) {
        ids.add(item._id);
      }
    }
    return ids;
  });

  /**
   * References to every `#itemEl` wrapper in the template.
   * Order matches the template render order: left group → center group → right group.
   * Used during measurement to read `offsetWidth` and build `_widthCache`.
   */
  readonly itemElements = viewChildren<ElementRef>('itemEl');

  /**
   * Items currently rendered in the row. Always includes pinned items.
   * Starts as a copy of `items()` and shrinks as items are moved to `hiddenItems`.
   */
  readonly visibleItems = signal<ItemField[]>([]);

  /**
   * Items that did not fit in the row and are shown in the "More" dropdown.
   * Populated by `_recalculate()`; empty when all items fit.
   */
  readonly hiddenItems = signal<ItemField[]>([]);

  /** Controls visibility of the `app-header-dropdown-menu` panel. */
  readonly _isMenuOpen = signal(false);

  /**
   * `true` during the initial width-measurement phase.
   * While `true`, the CSS class `--measuring` applies `visibility: hidden`
   * to the row, preventing a visible flash of all items before overflow is resolved.
   */
  readonly _measuring = signal(false);

  /**
   * `visibleItems()` split by position group for the template `@for` loops.
   * Items with no `position` fall back to CENTER (matching the service grouping logic).
   */
  readonly _visibleLeftItems = computed(() => this.visibleItems().filter((i) => i.position === PositionType.LEFT));
  readonly _visibleCenterItems = computed(() => this.visibleItems().filter((i) => i.position === PositionType.CENTER || !i.position));
  readonly _visibleRightItems = computed(() => this.visibleItems().filter((i) => i.position === PositionType.RIGHT));

  /**
   * Set of IDs for pinned items in the current `items()` array.
   * Recomputed only when `items` changes, then read cheaply by `_recalculate()`.
   */
  private readonly _pinnedIds = computed(() => {
    const pinned = new Set<string>();
    for (const item of this.items()) {
      if (this._isPinned(item)) pinned.add(item._id);
    }
    return pinned;
  });

  /**
   * `item._id → offsetWidth` map built during measurement.
   * Keyed by ID (not by array index) because `itemElements` are in template order
   * (left → center → right) while `items()` is sorted by `order` field — the two
   * orderings can differ, so index-based mapping would assign wrong widths.
   */
  private _widthCache = new Map<string, number>();
  /** Active `ResizeObserver` instance; created once after first measurement. */
  private _resizeObserver: ResizeObserver | null = null;
  /**
   * Per-item `ResizeObserver` that watches every `#itemEl` wrapper.
   * When a child component (e.g. `app-authentication-field`) renders its content
   * asynchronously after initial measurement (effects, skeleton → real content),
   * its wrapper's `offsetWidth` changes. This observer detects that change,
   * updates `_widthCache` for the affected item, and re-runs `_recalculate()`.
   * This is the primary fix for the AUTH item measuring too narrow on first render.
   */
  private _itemResizeObserver: ResizeObserver | null = null;
  /** Pending `requestAnimationFrame` handle used to debounce host resize callbacks. */
  private _rafId: number | null = null;
  /**
   * Pending `requestAnimationFrame` handle used to debounce item-level resize callbacks.
   * Kept separate from `_rafId` so host and item callbacks don't cancel each other.
   */
  private _itemRafId: number | null = null;
  /** `true` after the first successful measurement; gates `_recalculate()` calls. */
  private _initialized = false;
  /** Number of measurement retries attempted (max 10; guards against infinite loops). */
  private _measureRetries = 0;
  /**
   * Reference to the last `items()` array seen by the items-change effect.
   * Used for referential equality check to avoid re-measuring on unrelated signal updates.
   */
  private _prevItemsRef: ItemField[] | null = null;

  _toggleMenu(): void {
    this._isMenuOpen.update((open) => !open);
  }

  constructor() {
    /**
     * Re-initialize measurement whenever the `items` array reference changes.
     * Uses referential equality (`===`) so that re-renders caused by other signal
     * updates (e.g. auth state) do not trigger a full re-measure if the items
     * themselves are the same object.
     *
     * `untracked()` prevents the inner signal writes (`visibleItems.set`, etc.)
     * from being tracked as dependencies of this effect.
     */
    effect(() => {
      const items = this.items();
      if (typeof window === 'undefined') return;
      untracked(() => {
        const sameContent = this._prevItemsRef !== null && this._arraysEqualById(items, this._prevItemsRef);
        this._prevItemsRef = items;
        if (sameContent) return;

        if (items.length === 0) {
          this.visibleItems.set([]);
          this.hiddenItems.set([]);
          this._initialized = false;
          return;
        }

        // Show all items hidden while we measure — prevents flicker.
        this.visibleItems.set(items);
        this.hiddenItems.set([]);
        this._initialized = false;
        this._measureRetries = 0;
        this._measuring.set(true);
        afterNextRender(() => this._measureAndRecalculate(), { injector: this.injector });
      });
    });

    /**
     * Re-run `_recalculate()` when the active URL changes.
     * The URL itself doesn't affect which items fit, but some items change their
     * rendered width depending on active state (e.g. underline indicators can
     * shift layout), so a recalculation keeps things correct.
     */
    effect(() => {
      this.activeUrl();
      if (typeof window === 'undefined') return;
      untracked(() => {
        if (this._prevItemsRef) this._recalculate();
      });
    });

    /**
     * Re-observe item elements whenever the rendered set of `#itemEl` wrappers
     * changes (e.g. after `_recalculate()` hides/shows items and Angular updates
     * the DOM). The per-item `ResizeObserver` must track the current elements so
     * that any post-render width change (e.g. child component deferred content)
     * still triggers a cache update and re-calculation.
     */
    effect(() => {
      const els = this.itemElements(); // reactive dependency
      if (typeof window === 'undefined' || !this._initialized || els.length === 0) return;
      untracked(() => this._observeItemElements());
    });

    /**
     * Re-measure when translations finish loading.
     * `translate.use(lang)` is deferred via `requestIdleCallback` (up to 1s after
     * bootstrap), so the first measurement may run while `TranslatePipe` still
     * returns raw keys (e.g. "GENERAL.REGISTER_2" instead of "Registrar"). Raw
     * keys are wider than their translations, causing items to be measured as
     * too wide and incorrectly moved to the overflow dropdown. Items already
     * hidden are no longer in the DOM, so `_itemResizeObserver` never updates
     * their stale cache entries — they stay hidden permanently.
     *
     * Subscribing to `onLangChange` triggers a full reset + re-measurement once
     * the translated text is rendered, guaranteeing correct final widths.
     */
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (typeof window === 'undefined' || this.items().length === 0) return;
      this.visibleItems.set(this.items());
      this.hiddenItems.set([]);
      this._initialized = false;
      this._measureRetries = 0;
      this._measuring.set(true);
      afterNextRender(() => this._measureAndRecalculate(), { injector: this.injector });
    });

    /**
     * Re-measure after all fonts have loaded.
     * During SSR hydration or initial page load, fonts may not be available
     * when the first measurement runs, causing items to report narrower widths
     * than they will have once the custom font renders. This ensures the final
     * overflow state reflects the true rendered widths.
     */
    if (typeof document !== 'undefined' && document.fonts) {
      document.fonts.ready.then(() => {
        if (!this._measuring() && this._initialized && this.items().length > 0) {
          this.visibleItems.set(this.items());
          this.hiddenItems.set([]);
          this._measureRetries = 0;
          this._measuring.set(true);
          afterNextRender(() => this._measureAndRecalculate(), { injector: this.injector });
        }
      });
    }

    this.destroyRef.onDestroy(() => {
      this._resizeObserver?.disconnect();
      this._itemResizeObserver?.disconnect();
      if (this._rafId !== null) cancelAnimationFrame(this._rafId);
      if (this._itemRafId !== null) cancelAnimationFrame(this._itemRafId);
    });
  }

  /**
   * Reads the rendered `offsetWidth` of every item and populates `_widthCache`,
   * then calls `_recalculate()` and sets up the `ResizeObserver`.
   *
   * Why retry with `setTimeout`?
   * Angular's `afterNextRender` fires after the DOM is committed but sometimes
   * before the browser has performed layout (e.g. when custom elements or images
   * inside items haven't resolved yet). Retrying up to 10 times with a 20 ms
   * delay ensures we always get non-zero widths.
   *
   * Why build `templateOrder` instead of using `items()` order directly?
   * `viewChildren('itemEl')` returns elements in **template render order**:
   *   left-group items → center-group items → right-group items.
   * `items()` is sorted by the `order` field from the API and does not match
   * template order when items from different position groups are interleaved.
   * Mapping `els[i]` to `items()[i]` would assign the wrong width to items whose
   * template position differs from their `order` rank (e.g. AUTH at order=1 but
   * rendered last because it is RIGHT-positioned). Using the same left→center→right
   * grouping as the template gives a correct 1-to-1 mapping.
   */
  private _measureAndRecalculate(): void {
    const targetLength = this.items().length;
    if (targetLength === 0) return;

    const attemptMeasure = () => {
      const els = this.itemElements();

      // Wait until all #itemEl elements are in the DOM.
      if (els.length !== targetLength && this._measureRetries < 10) {
        this._measureRetries++;
        setTimeout(() => attemptMeasure(), 20);
        return;
      }

      // Reconstruct the template render order so each els[i] maps to the correct item.
      const allVisible = this.visibleItems(); // equals items() at measurement time
      const templateOrder = [
        ...allVisible.filter((i) => i.position === PositionType.LEFT),
        ...allVisible.filter((i) => i.position === PositionType.CENTER || !i.position),
        ...allVisible.filter((i) => i.position === PositionType.RIGHT),
      ];
      this._widthCache.clear();
      els.forEach((el, idx) => {
        if (idx < templateOrder.length) {
          this._widthCache.set(templateOrder[idx]._id, (el.nativeElement as HTMLElement).offsetWidth);
        }
      });

      // Any item with zero width means layout hasn't completed yet (e.g. a child
      // component effect hasn't run and rendered its content). Retry to ensure
      // all cached widths reflect the true final rendered sizes.
      if (this._widthCache.size > 0 && [...this._widthCache.values()].some((w) => w === 0) && this._measureRetries < 10) {
        this._measureRetries++;
        setTimeout(() => attemptMeasure(), 20);
        return;
      }

      this._initialized = true;
      this._recalculate();
      this._measuring.set(false);
      this._setupResizeObserver();
      this._observeItemElements();

      // Safety-net second pass: schedule one more measurement after the next
      // render cycle. Child component effects (e.g. app-authentication-field
      // setting buttonsHide = true) may run between the first measurement and
      // this render, expanding items beyond their measured widths. The second
      // pass detects those changes and corrects the overflow state.
      afterNextRender(
        () => {
          if (!this._initialized || this.items().length === 0) return;
          const allVisible = this.visibleItems();
          const templateOrder = [
            ...allVisible.filter((i) => i.position === PositionType.LEFT),
            ...allVisible.filter((i) => i.position === PositionType.CENTER || !i.position),
            ...allVisible.filter((i) => i.position === PositionType.RIGHT),
          ];
          const els = this.itemElements();
          if (els.length !== allVisible.length) return;
          els.forEach((el, idx) => {
            if (idx < templateOrder.length) {
              this._widthCache.set(templateOrder[idx]._id, (el.nativeElement as HTMLElement).offsetWidth);
            }
          });
          this._recalculate();
        },
        { injector: this.injector },
      );
    };

    attemptMeasure();
  }

  /**
   * Attaches per-item `ResizeObserver` entries to every `#itemEl` wrapper currently
   * in the DOM. When an item's rendered width changes after the initial measurement
   * (e.g. `app-authentication-field` rendering buttons via a deferred effect),
   * the observer updates that item's entry in `_widthCache` and re-runs
   * `_recalculate()` so the overflow logic uses the correct, final widths.
   *
   * A shared `_itemResizeObserver` instance is reused; individual elements are
   * un-observed and re-observed whenever the visible item set changes (see the
   * `itemElements` effect in the constructor).
   *
   * Debounced via a dedicated `_itemRafId` handle (separate from the host
   * observer's `_rafId`) so both observers can coexist without cancelling each
   * other's pending frames.
   */
  private _observeItemElements(): void {
    const els = this.itemElements();
    if (els.length === 0) return;

    // Lazily create the shared observer on first use.
    if (!this._itemResizeObserver) {
      this._itemResizeObserver = new ResizeObserver((entries) => {
        let changed = false;
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          const id = el.dataset['itemId'];
          if (id && this._widthCache.has(id)) {
            const newWidth = el.offsetWidth;
            if (newWidth !== this._widthCache.get(id)) {
              this._widthCache.set(id, newWidth);
              changed = true;
            }
          }
        }
        if (!changed) return;
        if (this._itemRafId !== null) cancelAnimationFrame(this._itemRafId);
        this._itemRafId = requestAnimationFrame(() => {
          this._itemRafId = null;
          this._recalculate();
        });
      });
    }

    // Disconnect all previously observed elements before re-observing the new set.
    this._itemResizeObserver.disconnect();

    const allVisible = this.visibleItems();
    const templateOrder = [
      ...allVisible.filter((i) => i.position === PositionType.LEFT),
      ...allVisible.filter((i) => i.position === PositionType.CENTER || !i.position),
      ...allVisible.filter((i) => i.position === PositionType.RIGHT),
    ];

    els.forEach((elRef, idx) => {
      const el = elRef.nativeElement as HTMLElement;
      // Tag the element with its item ID so the ResizeObserver callback can map
      // borderBox entries back to cache keys without needing a WeakMap.
      if (idx < templateOrder.length) {
        el.dataset['itemId'] = templateOrder[idx]._id;
        this._itemResizeObserver!.observe(el);
      }
    });
  }

  /**
   * Attaches a `ResizeObserver` to the host element so that `_recalculate()` is
   * called whenever the row width changes (e.g. browser resize, sidebar open/close).
   *
   * Debounced via `requestAnimationFrame`: multiple resize events fired in the
   * same frame are collapsed into a single recalculation, preventing the
   * ResizeObserver feedback loop that occurs when recalculation itself changes
   * the layout (e.g. More button appearing/disappearing shifts widths).
   *
   * Called only once, after the first successful measurement.
   */
  private _setupResizeObserver(): void {
    if (this._resizeObserver) return;
    const host = this.hostEl.nativeElement as HTMLElement;
    this._resizeObserver = new ResizeObserver(() => {
      if (this._rafId !== null) cancelAnimationFrame(this._rafId);
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this._recalculate();
      });
    });
    this._resizeObserver.observe(host);
  }

  /**
   * Determines which items fit in the row and updates `visibleItems` / `hiddenItems`.
   *
   * ## Algorithm
   *
   * **Step 1 – Check if everything fits.**
   * Sum cached widths + gaps for all items. The flex container has N items and
   * 2 spacers (N+2 flex children → N+1 gaps). A simple `reduce` over N items
   * counts N-1 gaps, so we add `2 * gap` to account for the two spacer gaps.
   * If the total fits within `containerWidth`, restore all items and return early.
   *
   * **Step 2 – Trim non-pinned items from the end.**
   * When overflow is detected, the More button will appear, consuming
   * `moreBtnWidth` (button width + its own gap slot). The available budget is:
   *
   *   budget = containerWidth − moreBtnWidth − safetyBuffer
   *
   * We iterate, removing the last non-pinned item each pass, until the remaining
   * set fits within the budget. Removal order is by `items()` array position
   * (last item first), which corresponds to rightmost-last in the configured
   * `order` field — furthest from pinned items.
   *
   * Pinned items (LOGO, AUTH) are **never** removed; they always stay visible.
   * If pinned items alone exceed the budget, we still keep all pinned items
   * and hide everything else — this is an edge case for very narrow viewports.
   *
   * **Step 3 – Guard against signal feedback loops.**
   * Only call `visibleItems.set()` / `hiddenItems.set()` if the result actually
   * changed (compared by item ID). This prevents the ResizeObserver from
   * triggering another recalculation when the layout change was caused by this
   * very recalculation (e.g. More button appearing shifts the container width).
   */
  private _recalculate(): void {
    const items = this.items();
    if (items.length === 0 || !this._initialized) return;

    const host = this.hostEl.nativeElement as HTMLElement;
    const containerWidth = host.offsetWidth;
    if (containerWidth <= 0) return;

    const fallbackWidth = 80; // used when an item isn't in the cache yet
    /**
     * Must match `--overflow-row-items-gap` in the SCSS.
     * Desktop: `--spacing-4` = 16px (SCSS default).
     * Mobile: `--spacing-2` = 8px (overridden via inline style binding in the template).
     * This value is used in both the "all fits" check and the budget-trim loop.
     */
    const gap = this.isMobile() ? 8 : 16;
    /**
     * Space reserved for the More button as a flex child.
     * +gap accounts for the extra gap slot the More button introduces between
     * itself and its neighbours when it appears.
     */
    const moreBtnWidth = (this.isMobile() ? MORE_BTN_WIDTH_MOBILE : MORE_BTN_WIDTH_DESKTOP) + gap;
    const safetyBuffer = 4; // prevents items from sitting exactly at the container edge

    // Read widths from the ID-keyed cache (order-independent).
    const getWidth = (item: ItemField) => this._widthCache.get(item._id) ?? fallbackWidth;

    // ── Step 1: check if all items fit without a More button ─────────────────
    // N items + 2 spacers = N+1 gaps; reduce gives N-1, so add 2*gap.
    const totalAll = items.reduce((sum, item, i) => sum + getWidth(item) + (i > 0 ? gap : 0), 0) + 2 * gap;

    if (totalAll <= containerWidth - safetyBuffer) {
      if (!this._arraysEqualById(items, this.visibleItems())) {
        this.visibleItems.set([...items]);
        this.hiddenItems.set([]);
      }
      return;
    }

    // ── Step 2: trim non-pinned items from the end until the set fits ─────────
    const budget = containerWidth - moreBtnWidth - safetyBuffer;

    const pinnedIds = this._pinnedIds();
    const keptNonPinned = items.filter((item) => !pinnedIds.has(item._id));
    const hidden: ItemField[] = [];

    while (keptNonPinned.length > 0) {
      // Rebuild the full kept set in items() order (pinned + remaining non-pinned).
      const keptSet = new Set(keptNonPinned.map((i) => i._id));
      const allKept = items.filter((item) => pinnedIds.has(item._id) || keptSet.has(item._id));
      // Same gap correction: N kept items + 2 spacers → add 2*gap.
      const total = allKept.reduce((sum, item, i) => sum + getWidth(item) + (i > 0 ? gap : 0), 0) + 2 * gap;
      if (total <= budget) break;
      // Remove the last non-pinned item; `unshift` preserves original order in `hidden`.
      hidden.unshift(keptNonPinned.pop()!);
    }

    // ── Step 3: apply results ─────────────────────────────────────────────────
    const hiddenSet = new Set(hidden.map((i) => i._id));
    const visible = items.filter((item) => !hiddenSet.has(item._id));

    // Safety: never produce an empty visible list (would hide the entire header).
    if (visible.length === 0 && items.length > 0) {
      visible.push(items[0]);
      const idx = hidden.findIndex((i) => i._id === items[0]._id);
      if (idx >= 0) hidden.splice(idx, 1);
    }

    // Skip signal updates if nothing changed to avoid ResizeObserver feedback loops.
    if (this._arraysEqualById(visible, this.visibleItems()) && this._arraysEqualById(hidden, this.hiddenItems())) {
      return;
    }

    this.visibleItems.set(visible);
    this.hiddenItems.set(hidden);
  }

  /** Compares two item arrays by ID only (no deep equality needed). */
  private _arraysEqualById(first: ItemField[], second: ItemField[]): boolean {
    if (first.length !== second.length) return false;
    return first.every((item, index) => item._id === second[index]._id);
  }

  /**
   * Returns `true` if the item must never be moved to the overflow dropdown.
   * An item is pinned when it is a MENU_TYPE identifier with a type of LOGO or AUTH.
   */
  private _isPinned(item: ItemField): boolean {
    return item.identifier_type === IdentifierType.MENU_TYPE && PINNED_MENU_TYPES.has(item.action?.menu_type);
  }
}
